import { useState } from 'react'
import type { Translation } from './client'
import { tw } from './strings'

/*
 * Reading somebody else's words in your own language.
 *
 * The author's text is translated for the reader when their languages differ
 * (see services/api/src/worx/translate.ts). The reader is always told it is a
 * translation and can open the original; the author's own screens never show
 * one. `pick` returns the translated field when there is one and the original
 * otherwise, so a screen renders the same way whether or not anything was
 * translated.
 */

export function TranslationNote({ from, original, onToggle }: {
  from: 'en' | 'es-PR'
  original: boolean
  onToggle: () => void
}) {
  return (
    <p className="w-translated muted small">
      {original ? '' : `${tw(from === 'es-PR' ? 'w.tr.fromSpanish' : 'w.tr.fromEnglish')} · `}
      <button type="button" className="link" onClick={onToggle}>
        {original ? tw('w.tr.showTranslation') : tw('w.tr.showOriginal')}
      </button>
    </p>
  )
}

/** For a screen with several translated fields and one toggle. */
export function useTranslation(translation: Translation | null | undefined) {
  const [original, setOriginal] = useState(false)
  const pick = <T extends string | null | undefined>(key: string, text: T): T | string =>
    translation && !original ? (translation.text[key] ?? text) : text
  const note = translation
    ? <TranslationNote from={translation.from} original={original} onToggle={() => setOriginal((v) => !v)} />
    : null
  return { pick, note }
}

/** One translated block with its own toggle: a message, a request. */
export function TranslatedBody({ text, translated, from, className }: {
  text: string
  translated?: string | null
  from?: 'en' | 'es-PR'
  className?: string
}) {
  const [original, setOriginal] = useState(false)
  if (!translated || !from) return <p className={className}>{text}</p>
  return (
    <>
      <p className={className}>{original ? text : translated}</p>
      <TranslationNote from={from} original={original} onToggle={() => setOriginal((v) => !v)} />
    </>
  )
}
